import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useChatMachine } from '@/machines/useChatMachine';
import { useUser } from '@/hooks/user/useUser';
import { getAvatars } from '@/helpers/avatarHelpers';
import { getInitials } from '@/pages/Profile/ProfileUtils';
import { ChatBubble } from './ChatBubble';
import { ChatInput } from './ChatInput';

export const ChatMessageArea = () => {
    const { messages, isStreaming, messageFeedback, placeholderMap, setMessageFeedback } = useChatMachine();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [animationCompleteIds, setAnimationCompleteIds] = useState<Set<string>>(new Set());

    const handleAnimationComplete = useCallback((messageId: string) => {
        setAnimationCompleteIds(prev => new Set(prev).add(messageId));
    }, []);

    const { user } = useUser();
    const avatars = getAvatars();
    const { userProfile } = user || {};
    const { avatarId, firstName, middleName, lastName } = userProfile || {};

    const userAvatarSrc = useMemo(() => {
        return avatars.find(a => a.id === avatarId)?.src;
    }, [avatars, avatarId]);

    const initials = useMemo(() => {
        return getInitials(firstName, middleName, lastName);
    }, [firstName, middleName, lastName]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isStreaming]);

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 min-h-0 overflow-hidden">
                <ScrollArea className="h-full pr-4" viewportRef={scrollRef}>
                    <div className="flex flex-col gap-3 py-4">
                        {messages.map(msg => (
                            <ChatBubble
                                key={msg.id}
                                message={msg}
                                userAvatarSrc={userAvatarSrc}
                                initials={initials ?? ''}
                                animationComplete={animationCompleteIds.has(msg.id)}
                                onAnimationComplete={handleAnimationComplete}
                                placeholderMap={placeholderMap}
                                messageFeedback={msg.serverMessageId ? messageFeedback[msg.serverMessageId] : undefined}
                                onFeedbackSubmitted={(messageId, feedbackType, feedbackId) =>
                                    setMessageFeedback(messageId, feedbackType, feedbackId)}
                            />
                        ))}
                    </div>
                </ScrollArea>
            </div>
            <ChatInput />
        </div>
    );
};
